<script lang="ts" setup>
import { computed } from "vue";
import type { PropType } from "vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import StepBar from "@/components/organisms/mypage/StepBar.vue";
import BgWaveCard from "@/components/organisms/common/BgWaveCard.vue";
import VerifiedStatusLabel from "@/components/organisms/mypage/VerifiedStatusLabel.vue";
import verifiedStatus from "@/models/verifiedStatus";
import { User } from "@/types/user/User";

type StatusLabel = "未登録" | "審査中" | "差戻し項目あり" | "認証済み" | "登録済み";

const toRegisterProfileRoute = { name: "RegistrationInformationAndStatus" };

const props = defineProps({
  isLoading: {
    type: Boolean,
    required: true
  },
  user: {
    type: Object as PropType<User>,
    required: true
  },
  hasBankAccount: {
    type: Boolean,
    required: true
  }
});

const isUserStatusUnverified = computed<boolean>(() => {
  return props.user.verifiedStatus === verifiedStatus.UNVERIFIED;
});
const isUserStatusRejected = computed<boolean>(() => {
  return props.user.verifiedStatus === verifiedStatus.REJECTED;
});
const isUserStatusVerified = computed<boolean>(() => {
  return props.user.verifiedStatus === verifiedStatus.VERIFIED;
});
const userVerifiedStatusLabel = computed<StatusLabel | null>(() => {
  if (props.isLoading) return null;
  if (!props.user.hasRegisteredProfile) return "未登録";
  if (isUserStatusUnverified.value) return "審査中";
  if (isUserStatusRejected.value) return "差戻し項目あり";
  if (isUserStatusVerified.value) return "認証済み";
  // 通常起こりえない
  return null;
});
const hasRejectedQualification = computed<boolean>(() => {
  return props.user.qualifications.some(qualification => {
    return qualification.status === verifiedStatus.REJECTED;
  });
});
const hasUnverifiedQualification = computed<boolean>(() => {
  return props.user.qualifications.some(qualification => {
    return qualification.status === verifiedStatus.UNVERIFIED;
  });
});
const hasVerifiedQualification = computed<boolean>(() => {
  return props.user.qualifications.some(qualification => {
    return qualification.status === verifiedStatus.VERIFIED;
  });
});
const allVerifiedQualifications = computed<boolean>(() => {
  if (props.user.qualifications.length === 0) {
    return false;
  }
  return props.user.qualifications.every(qualification => {
    return qualification.status === verifiedStatus.VERIFIED;
  });
});
const qualificationStatusLabel = computed<StatusLabel | null>(() => {
  if (props.isLoading) return null;
  if (props.user.qualifications.length === 0) return "未登録";
  if (hasRejectedQualification.value) return "差戻し項目あり";
  if (hasUnverifiedQualification.value) return "審査中";
  if (allVerifiedQualifications.value) return "認証済み";
  // 通常起こりえない
  return null;
});
const qualificationStatusLabelType = computed(() => {
  if (!qualificationStatusLabel.value) {
    return undefined;
  }
  if (hasUnverifiedQualification.value) {
    return "notice";
  }
  if (allVerifiedQualifications.value) {
    return "success"
  }
  return "danger";
});
const userBankAccountInfoLabel = computed<StatusLabel | null>(() => {
  if (props.isLoading) return null;
  return props.hasBankAccount ? "登録済み" : "未登録";
});
const userBusinessInfoLabel = computed<StatusLabel | null>(() => {
  if (props.isLoading) return null;
  return props.user.experiences.length > 0 ? "登録済み" : "未登録";
});
const isIncrementStepByQualification = computed(() => {
  // 資格が0件
  if (props.user.qualifications.length === 0) {
    return true;
  }
  // 認証済み資格が無く、差戻し資格がある
  return !hasVerifiedQualification.value && hasRejectedQualification.value;
});
const remainingSteps = computed<number>(() => {
  return [
    userVerifiedStatusLabel.value !== null && !["審査中", "認証済み"].includes(userVerifiedStatusLabel.value),
    isIncrementStepByQualification.value,
    userBankAccountInfoLabel.value === "未登録",
    userBusinessInfoLabel.value === "未登録"
  ].filter(c => c).length;
});
const underExaminationSteps = computed<number>(() => {
  return [
    userVerifiedStatusLabel.value === "審査中",
    hasUnverifiedQualification.value, // ここでは認証済み資格の有無は問わない
  ].filter(c => c).length;
});
const summaryText = computed(() => {
  return `${4 - remainingSteps.value} / 4${underExaminationSteps.value > 0 ? ` (${underExaminationSteps.value}項目審査中)` : ""}`;
});
const isShowUnderExamination = computed(() => {
  // 審査中の項目なし
  if (underExaminationSteps.value === 0) {
    return false;
  }
  // アカウント認証の審査中
  if (userVerifiedStatusLabel.value === "審査中") {
    return true;
  }
  // 以降は資格の審査中
  // この場合、認証済み資格があれば応募可能とみなし、審査中表示は行わない
  return !hasVerifiedQualification.value;
});

function getLabelType(label: StatusLabel) {
  return {
    "認証済み": "success",
    "登録済み": "success",
    "審査中": "notice",
    "未登録": "danger",
    "差戻し項目あり": "danger"
  }[label] as "success" | "notice" | "danger" || undefined;
}
</script>

<template>
  <div class="mx-3">
    <BgWaveCard class="mt-3 mb-4">
      <div class="skeleton" v-if="isLoading">
        <div class="skeleton-title" />
        <div class="skeleton-main mt-3" />
        <div class="skeleton-description mt-3" />
      </div>
      <div v-else-if="remainingSteps > 0" class="pt-3 d-flex flex-column">
        <div class="d-flex flex-column align-items-center mb-3">
          <p class="text-mild text-size-20 font-weight-bold">応募可能まで</p>
          <p class="text-mild text-size-20 font-weight-bold">
            あと<span class="mx-1 text-size-32 step">{{ remainingSteps }}</span
            >ステップ
          </p>
        </div>
        <div class="d-flex justify-content-center">
          <StepBar
            class="w-75"
            :maxValue="5"
            :currentValue="5 - remainingSteps"
            reverse
          />
        </div>
        <div
          class="mt-3 text-center text-primary text-size-12 font-weight-bold"
        >
          {{ summaryText }}
        </div>
      </div>
      <div v-else data-testid="verification-status">
        <p class="text-mild text-size-14">現在のステータス</p>
        <template v-if="isShowUnderExamination">
          <div class="py-3 d-flex justify-content-center">
            <FeatherIcon icon="circle" size="3x" class="text-yellow pr-2" />
            <p class="text-mild text-size-32">審査中</p>
          </div>
          <p class="text-mild text-size-14">
            認証されるまでお待ちください
          </p>
        </template>
        <template v-else>
          <div class="py-3 d-flex justify-content-center">
            <FeatherIcon
              icon="check-circle"
              size="3x"
              class="text-green pr-2"
            />
            <p class="text-mild text-size-32">認証済み</p>
          </div>
          <p class="text-mild text-size-14">
            お仕事にご応募可能です
          </p>
        </template>
      </div>
    </BgWaveCard>
  </div>
  <div class="section px-3">
    <p class="text-mild text-size-14 text-weight-500 mt-4 mb-1">
      登録状況の確認
    </p>
    <div class="text-mild text-size-10 pr-2">
      「未登録」「差戻し項目あり」の場合はタップして登録に進んでください<br />
      アカウント・資格認証は約1時間以内に完了します
    </div>
    <div class="bg-white border border-outline-color px-3 mt-1 round-4">
      <RouterLink
        :to="toRegisterProfileRoute"
        class="d-flex justify-content-between align-items-center py-3"
      >
        <p class="text-size-14">アカウント情報</p>
        <div class="d-flex justify-content-end align-items-center">
          <VerifiedStatusLabel
            v-if="userVerifiedStatusLabel"
            :text="userVerifiedStatusLabel"
            :type="getLabelType(userVerifiedStatusLabel)"
          />
          <FeatherIcon
            icon="chevron-right"
            size="1x"
            class="text-gray-05 ml-2"
          />
        </div>
      </RouterLink>
      <RouterLink
        :to="{ name: 'MypageQualificationList' }"
        class="d-flex justify-content-between align-items-center border-top border-outline-color py-3"
      >
        <p class="text-size-14">資格証</p>
        <div
          class="d-flex justify-content-end align-items-center"
          data-testid="qualification-status-label"
        >
          <VerifiedStatusLabel
            v-if="qualificationStatusLabel"
            :text="qualificationStatusLabel"
            :type="qualificationStatusLabelType"
          />
          <FeatherIcon
            icon="chevron-right"
            size="1x"
            class="text-gray-05 ml-2"
          />
        </div>
      </RouterLink>
      <RouterLink
        :to="{ name: 'SetBank' }"
        class="d-flex justify-content-between align-items-center border-top border-outline-color py-3"
      >
        <p class="text-size-14">給与口座</p>
        <div class="d-flex justify-content-end align-items-center">
          <VerifiedStatusLabel
            v-if="userBankAccountInfoLabel"
            :text="userBankAccountInfoLabel"
            :type="getLabelType(userBankAccountInfoLabel)"
          />
          <FeatherIcon
            icon="chevron-right"
            size="1x"
            class="text-gray-05 ml-2"
          />
        </div>
      </RouterLink>
      <RouterLink
        :to="{ name: 'MypageBusinessInfo' }"
        class="d-flex justify-content-between align-items-center border-top border-outline-color py-3"
      >
        <p class="text-size-14">経験・転職意欲</p>
        <div class="d-flex justify-content-end align-items-center">
          <VerifiedStatusLabel
            v-if="userBusinessInfoLabel"
            :text="userBusinessInfoLabel"
            :type="getLabelType(userBusinessInfoLabel)"
          />
          <FeatherIcon
            icon="chevron-right"
            size="1x"
            class="text-gray-05 ml-2"
          />
        </div>
      </RouterLink>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@mixin skeletonMixin {
  position: relative;
  overflow: hidden;
  background: $gray-02;
  border-radius: 5%;

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    content: "";
    background: linear-gradient(
      90deg,
      transparent,
      rgba($white, 0.5),
      transparent
    );
    animation: skeleton-animation 1.2s linear infinite;
  }
}

.skeleton {
  .skeleton-title {
    @include skeletonMixin;

    width: 30%;
    height: 20px;
  }

  .skeleton-main {
    @include skeletonMixin;

    width: 70%;
    height: 48px;
    margin: 0 auto;
  }

  .skeleton-description {
    @include skeletonMixin;

    width: 90%;
    height: 60px;
    margin: 0 auto;
  }
}

@keyframes skeleton-animation {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.step {
  color: #e42a2b;
}
</style>
